import { useEffect, useId, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';
import type { RawComponent } from '@/features/courses/useCourse';
import { useCourses } from '@/features/courses/useCourses';

import { getCopyTargetCourseOptions } from './courseOptionUtils';
import { useCopyComponent } from './useCopyComponent';
import styles from './CopyComponentDialog.module.css';

interface CopyComponentDialogProps {
  component: RawComponent;
  componentTitle: string;
  currentCourseId: string;
  currentCourseTitle: string;
  onOpenChange(open: boolean): void;
  onSuccess(message: string): void;
  open: boolean;
}

function getErrorMessage(error: unknown) {
  return error instanceof Error && error.message
    ? error.message
    : 'Unable to copy this chapter right now.';
}

export function CopyComponentDialog({
  component,
  componentTitle,
  currentCourseId,
  currentCourseTitle,
  onOpenChange,
  onSuccess,
  open,
}: CopyComponentDialogProps) {
  const selectId = useId();
  const coursesQuery = useCourses();
  const copyComponent = useCopyComponent(currentCourseId);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const courses = coursesQuery.data ?? [];
  const userCourseIds =
    coursesQuery.data?.userCourseIds && coursesQuery.data.userCourseIds.length > 0
      ? coursesQuery.data.userCourseIds
      : courses.map((course) => course.id);
  const courseOptions = useMemo(
    () =>
      getCopyTargetCourseOptions({
        courses,
        currentCourseId,
        currentCourseTitle,
        userCourseIds,
      }),
    [courses, currentCourseId, currentCourseTitle, userCourseIds],
  );
  const selectedCourse = courseOptions.find(
    (option) => option.id === selectedCourseId,
  );
  const onlyCurrentCourse =
    courseOptions.length === 1 && courseOptions[0]?.id === currentCourseId;

  useEffect(() => {
    if (!open) {
      setSelectedCourseId('');
    }
  }, [open]);

  async function handleCopy() {
    if (!selectedCourse) {
      return;
    }

    await copyComponent.mutateAsync({
      component,
      toCourseId: selectedCourse.id,
    });
    onSuccess(`Copied to ${selectedCourse.label}`);
    onOpenChange(false);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <div>
              <Dialog.Title className={styles.title}>
                Copy "{componentTitle}"
              </Dialog.Title>
              <Dialog.Description asChild>
                <p className={styles.description}>
                  Copy this chapter to a course you teach.
                </p>
              </Dialog.Description>
            </div>
          </div>

          <div className={styles.body}>
            <label className={styles.field} htmlFor={selectId}>
              <span className={styles.label}>Copy this chapter to a course</span>
              <select
                className={styles.select}
                disabled={coursesQuery.isPending || copyComponent.isPending}
                id={selectId}
                onChange={(event) => setSelectedCourseId(event.target.value)}
                value={selectedCourseId}
              >
                <option value="">
                  {coursesQuery.isPending ? 'Loading courses…' : 'Select a course'}
                </option>
                {courseOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label} · {option.id}
                    {option.isCurrent ? ' (current)' : ''}
                  </option>
                ))}
              </select>
            </label>

            {onlyCurrentCourse ? (
              <p className={styles.helperText}>
                You don't have any other courses to copy to yet. You can still
                copy this chapter into the current course.
              </p>
            ) : null}

            {copyComponent.isError ? (
              <p aria-live="polite" className={styles.error} role="alert">
                {getErrorMessage(copyComponent.error)}
              </p>
            ) : null}
          </div>

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button disabled={copyComponent.isPending} variant="secondary">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              disabled={!selectedCourse || copyComponent.isPending}
              onClick={() => {
                void handleCopy();
              }}
            >
              {copyComponent.isPending ? 'Copying…' : 'Copy'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
